import React from 'react';
import {
    DiscoverEventCardProps,
    EXPERIMENT_SEARCH_CARD_MAP_DESKTOP,
    EXPERIMENT_SEARCH_CARD_MAP_MOBILE,
} from '../../components/DiscoverEventCard';

import { FormattedEvent } from '@eventbrite/event-renderer';
import classNames from 'classnames';
import {
    useShowLikesExperiment,
    useShowLocalityVenueExperiment,
} from '../../pages/search/hooks';
import './SearchEventCardStyleSwitcher.scss';

export type SearchEventCardStylesSwitcherProps = DiscoverEventCardProps & {
    forceVariant?: 'desktop' | 'mobile';
};

const mobileSearchShareOptions = {
    utmOptions: {
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-medium': 'discovery',
        'utm-share-source': 'mobile-search-results',
        'utm-term': '',
    },
};

// This changes the locations associated with an event for the locality venue experiment
// so that we avoid introducing experimentation code into the event cards. If the experiment
// is successful, the logic to use cities instead of neighborhoods should be in event-renderer
const getEventProp = (
    event: FormattedEvent,
    isShowLocalityVenueExperimentEnabled: boolean,
): FormattedEvent => {
    if (isShowLocalityVenueExperimentEnabled) {
        // Users in the experiment should not see neighborhood or borough locations
        const filteredLocations = (event.locations || []).filter(
            (location) => !['neighbourhood', 'borough'].includes(location.type),
        );

        // If the venue has a city name, prioritize that to match what the organizer
        // sees in their venue settings
        if (event.venue?.address?.city) {
            filteredLocations.unshift({
                type: 'locality',
                name: event.venue.address.city,
                id: event.venue.id,
            });
        }

        return {
            ...event,
            locations: filteredLocations,
        };
    }

    return event;
};

export const SearchEventCardStyleSwitcher: React.FunctionComponent<
    SearchEventCardStylesSwitcherProps
> = ({ forceVariant, ...props }) => {
    const { MobileEventCard, DesktopEventCard } = useGetEventCardTypes();

    const showMobile = forceVariant !== 'desktop';
    const showDesktop = forceVariant !== 'mobile';
    const shouldHandleVisibility = !forceVariant;
    const isShowLikesExperimentEnabled = useShowLikesExperiment(
        props.event.isPromoted,
    );
    const isShowLocalityVenueExperimentEnabled =
        useShowLocalityVenueExperiment();

    const event = getEventProp(
        props.event,
        isShowLocalityVenueExperimentEnabled,
    );

    return (
        <>
            {showMobile && (
                <div
                    className={classNames(
                        'discover-search-mobile-card',
                        shouldHandleVisibility &&
                            'discover-search-mobile-card--hiddeable',
                    )}
                    key={`discover-mobile-event-card-${props.event.id}`}
                >
                    <MobileEventCard
                        {...props}
                        event={event}
                        shareOptions={mobileSearchShareOptions}
                        isShowLikesExperimentEnabled={
                            isShowLikesExperimentEnabled
                        }
                    />
                </div>
            )}
            {showDesktop && (
                <div
                    className={classNames(
                        'discover-search-desktop-card',
                        shouldHandleVisibility &&
                            'discover-search-desktop-card--hiddeable',
                    )}
                    key={`discover-desktop-event-card-${props.event.id}`}
                >
                    <DesktopEventCard {...props} event={event} />
                </div>
            )}
        </>
    );
};

function useGetEventCardTypes() {
    const MobileEventCard = EXPERIMENT_SEARCH_CARD_MAP_MOBILE['control'];
    const DesktopEventCard = EXPERIMENT_SEARCH_CARD_MAP_DESKTOP['control'];

    return {
        MobileEventCard: MobileEventCard,
        DesktopEventCard: DesktopEventCard,
    };
}
